import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Col, Row } from "react-bootstrap";

import KpiCard from "../components/dashboard/KpiCard";
import ReconBreakdownDonut from "../components/dashboard/ReconBreakdownDonut";
import ReconRateTrend from "../components/dashboard/ReconRateTrend";
import TermsUrgencyBar from "../components/dashboard/TermsUrgencyBar";
import TopSuppliersReconciliation from "../components/dashboard/TopSuppliersReconciliation";

// Old dashboard imports (preserved for later use)
// import DashboardKPI from "../components/DashboardKPI";
// import DashboardKPITable from "../components/DashboardKPITable";
// import DashboardKPITop10 from "../components/DashboardKPITop10";
// import DashboardChartAged from "../components/DashboardChartAged";
// import dashboardData from "../data/TableData.json";
// import dashboardTop10Data from "../data/Top10TableData.json";

const currencyFormat = new Intl.NumberFormat("en-CA", {
   style: "currency",
   currency: "CAD",
   minimumFractionDigits: 0,
   maximumFractionDigits: 0,
   currencyDisplay: "narrowSymbol",
});

const toNumber = (value) => {
   const n = Number(value);
   return Number.isFinite(n) ? n : 0;
};

const buildKpiCards = (payload) => {
   const card = Array.isArray(payload?.cards) && payload.cards.length > 0 ? payload.cards[0] : {};

   const unreconciledAmount = toNumber(card?.unreconciled_statement_current?.unreconciled_amount);
   const unreconciledCount = toNumber(card?.unreconciled_statement_current?.unreconciled_count);
   const oldestDate = card?.oldest_statements?.date || "-";
   const uploadCountToday = toNumber(card?.upload_count_today?.uploaded);
   const statementsPending = toNumber(card?.pending?.statements_pending);
   const requiresReview = toNumber(card?.review?.requires_review);
   const supplierRequired = toNumber(card?.supplier_required?.supplier_required);
   const lowConfidence = toNumber(card?.low_confidence?.low_confidence);
   const periodStart = card?.period?.current_month_start || "-";
   const periodEnd = card?.period?.current_month_end || "-";

   return [
      {
         label: "Unreconciled Statements",
         icon: "ri-file-warning-line",
         value: currencyFormat.format(unreconciledAmount),
         comparePeriod: `${Math.round(unreconciledCount).toLocaleString()} statements`,
      },
      {
         label: "Oldest Statement",
         icon: "ri-calendar-event-line",
         value: oldestDate,
         comparePeriod: `Period: ${periodStart} to ${periodEnd}`,
      },
      {
         label: "Uploads Today",
         icon: "ri-file-upload-line",
         value: Math.round(uploadCountToday).toLocaleString(),
      },
      {
         label: "Statements Pending",
         icon: "ri-time-line",
         value: Math.round(statementsPending).toLocaleString(),
      },
      {
         label: "Requires Review",
         icon: "ri-search-eye-line",
         value: Math.round(requiresReview).toLocaleString(),
      },
      {
         label: "Supplier Required",
         icon: "ri-user-received-2-line",
         value: Math.round(supplierRequired).toLocaleString(),
      },
      {
         label: "Low Confidence",
         icon: "ri-error-warning-line",
         value: Math.round(lowConfidence).toLocaleString(),
      },
   ];
};

const DONUT_DATA = {
   series: [4230, 512, 285, 198, 310],
   labels: ["Matched", "Partial Match", "No Invoice", "No Statement", "Ignored"],
   centerValue: "92.3%",
   centerLabel: "Reconciled",
   legendItems: [
      { color: "#506fd9", label: "Matched", value: "4,230" },
      { color: "#6b8de6", label: "Partial", value: "512" },
      { color: "#85b6ff", label: "No Invoice", value: "285" },
      { color: "#41505f", label: "No Statement", value: "198" },
      { color: "#c1d4f6", label: "Ignored", value: "310" },
   ],
};

const TREND_DATA = {
   series: [
      { name: "Unreconciled", type: "column", data: [62, 55, 48, 51, 47, 42, 58, 53, 49, 44, 47, 41] },
      { name: "Recon Rate", type: "area", data: [78, 80, 83, 82, 85, 88, 84, 87, 89, 91, 90, 92.3] },
   ],
   categories: ["Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan"],
};

// Maps getTermsUrgency.sz buckets -> the TermsUrgencyBar contract.
// Mirrors the urgency_bucket keys produced by the statement_effective_terms view.
const URGENCY_DISPLAY = [
   { key: "OVERDUE", label: "Overdue", color: "#41505f" },
   { key: "DUE_THIS_WEEK", label: "Due This Week", color: "#506fd9" },
   { key: "DUE_2_WEEKS", label: "Due in 2 Weeks", color: "#6b8de6" },
   { key: "DUE_30_DAYS", label: "Due in 30 Days", color: "#85b6ff" },
   { key: "LATER", label: "30+ Days Out", color: "#c1d4f6" },
];

const buildUrgencyData = (payload) => {
   const rows = Array.isArray(payload) ? payload : [];
   const byBucket = Object.fromEntries(rows.map((r) => [r.bucket, r]));
   const counts = URGENCY_DISPLAY.map((d) => toNumber(byBucket[d.key]?.cnt));
   const total = counts.reduce((a, b) => a + b, 0) || 1;
   return {
      summaryCards: URGENCY_DISPLAY.map((d) => ({
         label: d.label,
         color: d.color,
         amount: currencyFormat.format(toNumber(byBucket[d.key]?.amount)),
      })),
      progressSegments: URGENCY_DISPLAY.map((d, i) => ({
         now: Math.round((counts[i] / total) * 100),
         label: counts[i],
         color: d.color,
      })),
      series: URGENCY_DISPLAY.map((d, i) => ({ name: d.label, data: [counts[i]] })),
      categories: ["Unreconciled"],
   };
};

const TOP_SUPPLIERS_DATA = [
   {
      name: "Company 1",
      total_amt: 1500.0,
      recon_amt: 999.0,
      outstanding_amt: 501.0,
      recon_percent: "66.6%",
   },
   {
      name: "Company 2",
      total_amt: 2000.0,
      recon_amt: 1000.0,
      outstanding_amt: 1000.0,
      recon_percent: "50.0%",
   },
];

const fetchKpiMetrics = async () => {
   const res = await fetch("/getkpimetrics.sz", {
      method: "POST",
      credentials: "include",
   });
   if (!res.ok) throw new Error("Failed to fetch KPI metrics");
   const json = await res.json();
   if (json.st !== 1) throw new Error(json.mg || "API error");
   return json.payload;
};

const fetchTermsUrgency = async () => {
   const res = await fetch("/getTermsUrgency.sz", {
      method: "POST",
      credentials: "include",
   });
   if (!res.ok) throw new Error("Failed to fetch terms urgency");
   const json = await res.json();
   if (json.st !== 1) throw new Error(json.mg || "API error");
   return json.payload;
};

const fetchTrendData = async () => {
   const res = await fetch("/getkpireconratemonth.sz", {
      method: "POST",
      credentials: "include",
   });
   if (!res.ok) throw new Error("Failed to fetch trend data");
   const json = await res.json();
   if (json.st !== 1) throw new Error(json.mg || "API error");
   return json.payload;
};

const fetchDonutData = async () => {
   const res = await fetch("/getkpibreakdown.sz", {
      method: "POST",
      credentials: "include",
   });
   if (!res.ok) throw new Error("Failed to fetch donut data");
   const json = await res.json();
   if (json.st !== 1) throw new Error(json.mg || "API error");
   return json.payload;
};

const buildTrendData = (payload) => {
   if (!payload) return TREND_DATA; // fallback to dummy data
   
   // Extract kpi object from payload array
   const kpi = Array.isArray(payload) && payload.length > 0 ? payload[0]?.kpi : null;
   
   if (!kpi || !kpi.series || !kpi.categories) {
      return TREND_DATA; // fallback if structure is invalid
   }
   
   return {
      series: kpi.series,
      categories: kpi.categories,
   };
};

const buildDonutData = (payload) => {
   if (!payload) return DONUT_DATA; // fallback to dummy data
   
   // Extract kpi object from payload array
   const kpi = Array.isArray(payload) && payload.length > 0 ? payload[0]?.kpi : null;
   
   if (!kpi) return DONUT_DATA; // fallback if structure is invalid
   
   // Extract values
   const matched = toNumber(kpi.full_match);
   const partial = toNumber(kpi.partial_match);
   const unmatched = toNumber(kpi.unmatched);
   const ignored = toNumber(kpi.ignored);
   const overAllocated = toNumber(kpi.duplicate_over_allocated);
   
   const reconciled = matched + partial;
   const reconcilableTotal = matched + partial + unmatched;
   const reconRate = reconcilableTotal > 0 ? ((reconciled / reconcilableTotal) * 100).toFixed(1) : "0.0";
   
   return {
      series: [matched, partial, unmatched, ignored],
      labels: ["Matched", "Partial Match", "Unmatched", "Ignored"],
      centerValue: `${reconRate}%`,
      centerLabel: "Reconciled",
      legendItems: [
         { color: "#506fd9", label: "Matched", value: matched.toLocaleString() },
         { color: "#6b8de6", label: "Partial", value: partial.toLocaleString() },
         { color: "#41505f", label: "Unmatched", value: unmatched.toLocaleString() },
         { color: "#c1d4f6", label: "Ignored", value: ignored.toLocaleString() },
      ],
   };
};

export default function Home() {
   const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
   const [skin, setSkin] = useState(currentSkin);

   const {
      data: kpiPayload,
      isLoading: kpiLoading,
      isError: kpiError,
      refetch: kpiRefetch,
   } = useQuery({
      queryKey: ["kpi", "metrics"],
      queryFn: fetchKpiMetrics,
      staleTime: 5 * 60 * 1000,
   });

   const {
      data: trendPayload,
      isLoading: trendLoading,
      isError: trendError,
      refetch: trendRefetch,
   } = useQuery({
      queryKey: ["trend", "data"],
      queryFn: fetchTrendData,
      staleTime: 5 * 60 * 1000,
   });

   const {
      data: donutPayload,
      isLoading: donutLoading,
      isError: donutError,
      refetch: donutRefetch,
   } = useQuery({
      queryKey: ["donut", "data"],
      queryFn: fetchDonutData,
      staleTime: 5 * 60 * 1000,
   });

   const {
      data: urgencyPayload,
      isLoading: urgencyLoading,
   } = useQuery({
      queryKey: ["terms", "urgency"],
      queryFn: fetchTermsUrgency,
      staleTime: 5 * 60 * 1000,
   });

   const kpiCards = buildKpiCards(kpiPayload);
   const trendData = buildTrendData(trendPayload);
   const donutData = buildDonutData(donutPayload);
   const urgencyData = buildUrgencyData(urgencyPayload);

   const switchSkin = (skin) => {
      if (skin === "dark") {
         const btnWhite = document.getElementsByClassName("btn-white");
         for (const btn of btnWhite) {
            btn.classList.add("btn-outline-primary");
            btn.classList.remove("btn-white");
         }
      } else {
         const btnOutlinePrimary = document.getElementsByClassName("btn-outline-primary");
         for (const btn of btnOutlinePrimary) {
            btn.classList.remove("btn-outline-primary");
            btn.classList.add("btn-white");
         }
      }
   };

   switchSkin(skin);

   useEffect(() => {
      switchSkin(skin);
   }, [skin]);

   return (
      <>
         <Header onSkin={setSkin} />
         <div className="main main-app p-3 p-lg-4">

            <div className="d-flex align-items-center justify-content-between mb-4">
               <div>
                  <h4 className="main-title mb-0">Reconciliation Dashboard</h4>
               </div>
            </div>

            {/* === Tier 1: KPI Cards — Single row (all 7) === */}
            <Row className="g-2 mb-3">
               {(kpiLoading ? Array.from({ length: 7 }) : kpiCards).map((card, index) => (
                  <Col key={index} md={3} xl>
                     <KpiCard {...(card || {})} isLoading={kpiLoading} isError={kpiError} onRetry={kpiRefetch} compact />
                  </Col>
               ))}
            </Row> 
            {/* === Tier 1: Charts === */}
            <Row className="g-3 mb-3">
               <Col md="4">
                  <ReconBreakdownDonut data={donutData} isLoading={donutLoading} isError={donutError} onRetry={donutRefetch} />
               </Col>
               <Col md="8">
                      <TopSuppliersReconciliation data={TOP_SUPPLIERS_DATA} isLoading={false} />
           
                  {/* <ReconRateTrend data={trendData} isLoading={trendLoading} isError={trendError} onRetry={trendRefetch} /> */}
               </Col>
            </Row>

            <Row className="g-3 mb-4">
               <Col xs="12">
                  <TermsUrgencyBar data={urgencyData} isLoading={urgencyLoading} />
               </Col>
            </Row>

            {/* === Old Dashboard Content (preserved) =  ==
            <Row className="g-3 flex-wrap justify-content-start">
               <DashboardKPI />
               <DashboardKPITable dashboardData={dashboardData} />
               <DashboardChartAged />
               <DashboardKPITop10 dashboardData={dashboardTop10Data} />
            </Row>
            === End Old Dashboard Content === */}

            <Footer />
         </div>
      </>
   );
}
